import { mapKeys, find, get, set, chain, reduce } from "lodash"
import { createSelector } from "reselect"
import { getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { makeSelectors, computeFactorsWithMetada, computeFactorViewCategories, computeFactorViewRisk, computeFactorVaR, computeSummaryRows, computeTotalRow, computeFactorExposure, computeCurrentFactors } from "helpers/factor-view"
import { DEFAULT_STATE } from "modules/analyze-factors-risk/model"
import { getModelCategories, getModelFactors } from "selectors/modelMetadata"

const EMPTY_ARRAY = []

// Apart from the state object key, the following selectors are all indentical to the intraday-risk-factors selectors
const [
  getRiskSummary,
  getValueAtRiskSummary,
  getRawAnalyzeFactorsRisk,
  getRawValueAtAnalyzeFactorsRisk,
  getRawFactorExposure,
  selectedFactorCategory,
] = makeSelectors("analyzeFactorsRisk")

const getFactorCovariances = state => get(state, "global.data.factorCovariances.data.model.factors", EMPTY_ARRAY)

const getFactorVolatility = createSelector(
  [getFactorCovariances],
  (factors) => reduce(factors, (result, factor) => set(result, factor.id, Math.sqrt(
    chain(factor)
      .get(["covariance", "factors"])
      .find({ id: factor.id })
      .get("annualizedPercentSquared")
      .value(),
  )), {}),

)

const getMappedRiskFactors = createSelector(
  [getRawAnalyzeFactorsRisk],
  (riskFactors) => mapKeys(riskFactors, (factor) => factor.id),
)

const getIsComposite = (state, props) => get(props, "isComposite", false)

export const getTotalRow = createSelector(
  [getRiskSummary, getValueAtRiskSummary, getIsComposite, getCurrency],
  computeTotalRow,
)

export const getSummaryRows = createSelector(
  [getRiskSummary, getValueAtRiskSummary],
  computeSummaryRows,
)

export const getFactorExposure = createSelector(
  [getRawFactorExposure],
  computeFactorExposure,

)

export const getFactorValueAtRisk = createSelector(
  [getRawValueAtAnalyzeFactorsRisk],
  computeFactorVaR,
)

// Contains only factors that have non-nil values for at least one of the following [risk, currencyExposure, valueAtRisk]
export const getFactorsWithMetadata = createSelector(
  [getMappedRiskFactors, getFactorExposure, getFactorValueAtRisk, getModelFactors, getFactorVolatility],
  computeFactorsWithMetada,
)

export const getFactorCategories = createSelector(
  [getFactorsWithMetadata],
  computeFactorViewRisk,
)

// Contains only non-nil factors categories
export const getCategories = createSelector(
  [getModelCategories, getSummaryRows, getFactorCategories],
  computeFactorViewCategories,
)

export const getSelectedFactorCategory = createSelector(
  [selectedFactorCategory, getCategories],
  (selected, categories) => selected === DEFAULT_STATE.selectedFactorCategory || find(categories, ({ id }) => id === selected) ? selected : DEFAULT_STATE.selectedFactorCategory,
)

export const getCurrentFactors = createSelector(
  [getFactorsWithMetadata, getSelectedFactorCategory],
  computeCurrentFactors,
)
